import { DateLocale, TimeZoneString } from "../models/locale.model";
import { SECONDS_IN_ONE_DAY } from "./time.tools";

/** 
 * Unix Timestamp (the number of seconds since the Unix Epoch), represents a Date in UTC, used to transfer data in json (ajax routes). 
 * It is in fact a number, but we use a different interface to enforce type checking. You can use valueOf or toTimestamps to convert to or from number, 
 * or you can use toDate or fromDate to convert to or from TsDate.
 */
export type UnixTimestamp = number & {
    /** isTimestamp cannot be set... here for typechecking */
    isTimestamp: true
}

/** Interval of timestamp dates */
export interface UnixTimestampInterval {
    start: UnixTimestamp;
    finish: UnixTimestamp;
}

export interface TsDateInterval {
    start: TsDate;
    finish: TsDate;
}

/** Interval of timestamp dates with DTO properties naming convention */
export interface DtoInterval {
    startTs: UnixTimestamp;
    finishTs: UnixTimestamp;
}

/** Interval of possibly undefined timestamp dates */
export interface UnixTimestampIndeterminateInterval {
    start?: UnixTimestamp;
    finish?: UnixTimestamp;
}

/** DATE_UNITS contains the units sorted by importance */
export const DATE_UNITS = ["year", "quarter", "month", "week", "day", "hour", "minute", "second"] as const;
export type TsDateUnit = typeof DATE_UNITS[number];
export type TsDuration = Partial<Record<TsDateUnit, number>>;
export interface TsSimpleDuration {
    unit: TsDateUnit;
    unitCount: number;
}

export type TsDurationParam = TsDuration | TsSimpleDuration;

export enum DAYS {
    MONDAY = 1,
    TUESDAY = 2,
    WEDNESDAY = 3,
    THURSDAY = 4,
    FRIDAY = 5,
    SATURDAY = 6,
    SUNDAY = 7,
}


export const AGENDA_MODES = ["month", "week", "day"] as const;
export type AgendaMode = typeof AGENDA_MODES[number];

export interface TsDateObjectValues {

    /**
     * Get the year (ex : 2017)
     */
    get year(): number;

    /**
     * Get the month (1-12).
     */
    get month(): number;

    /**
     * Get the day of the month (1-30ish).
     */
    get day(): number;

    /**
     * Get the hour of the day (0-23).
     */
    get hour(): number;

    /**
     * Get the minute of the hour (0-59).
     */
    get minute(): number;

    /**
     * Get the second of the minute (0-59).
     */
    get second(): number;

    /**
     * Get the ISO week year
     * @see https://en.wikipedia.org/wiki/ISO_week_date
     *
     * (2014-12-31).weekYear //=> 2015
     */
    get weekYear(): number;

    /**
     * Get the ISO week number of the week year (1-52ish).
     * @see https://en.wikipedia.org/wiki/ISO_week_date
     *
     * (2017-05-25).weekNumber //=> 21
     */
    get weekNumber(): number;

    /**
     * Get the day of the week.
     * 1 is Monday and 7 is Sunday
     * @see https://en.wikipedia.org/wiki/ISO_week_date
     */
    get weekday(): number;

    /**
     * Get the ordinal (meaning the day of the year)
     *
     * (2017-05-25).ordinal //=> 145
     */
    get ordinal(): number;

    /**
     * Seconds since day start
     *
     * (2017-05-25 01:05:06).minuteOfDay //=> 1*60+5
     */
    get minuteOfDay(): number;

    /**
     * Seconds since day start
     *
     * (2017-05-25 01:05:06).secondOfDay //=> 1*60*60+5+60+6
     */
    get secondOfDay(): number;
}

export interface TsDate extends TsDateObjectValues {

    /**
     * Adding hours, minutes, seconds increases the timestamp by the right number of seconds. 
     * Adding days, months, or years shifts the calendar, accounting for DSTs and leap years along the way.
     * Thus, `dt.add({ hours: 24 })` may result in a different time than `dt.add({ days: 1 })` if there's a DST shift in between.
     *
     * add({ minutes: 15 }) //~> in 15 minutes
     * add({ days: 1 }) //~> this time tomorrow
     * add({ days: -1 }) //~> this time yesterday
     * add({ hours: 3, minutes: 13 }) //~> in 3 hr, 13 min
     * add({ hours: 3, minutes: - 3 }) //~> in 2 hr, 57 min
     * 
     * @returns a newly-constructed TsDate.
     */
    add(duration: TsDurationParam): TsDate;

    /**
     * See {@link add}
     */
    subtract(duration: TsDurationParam): TsDate;

    /**
     * "Set" the values of specified units.
     *
     * setValues({ year: 2017 })
     * setValues({ hour: 8, minute: 30 })
     * setValues({ weekday: 5 })
     * setValues({ year: 2005, ordinal: 234 })
     * 
     * @returns a newly-constructed TsDate.
     */
    setValues(values: Partial<TsDateObjectValues>): TsDate;

    /**
     * "Build" a TsDate corresponding to the beginning of a unit of time from passed date
     * 
     * "2021-02-27 17:22:12" .beginOf('hour'); //=> '2021-02-27 17:00:00'
     * 
     * @returns a newly-constructed TsDate.
     */
    beginOf(unit: TsDateUnit): TsDate;

    /**
     * "Build" a TsDate corresponding to the NEXT beginning of a unit of time from passed date.
     * 
     * "2021-02-27 17:22:12" .beginOfNext('hour'); //=> '2021-02-27 18:00:00'
     * 
     * @returns a newly-constructed TsDate.
     */
    beginOfNext(unit: TsDateUnit): TsDate;

    /**
     * "Build" a TsDate corresponding to the PREVIOUS beginning of a unit of time from passed date.
     * 
     * "2021-02-27 17:22:12" .beginOfPrevious('day'); //=> '2021-02-26 00:00:00'
     * 
     * @returns a newly-constructed TsDate.
     */
    beginOfPrevious(unit: TsDateUnit): TsDate;

    // ! there is no endOf function on TsDate because most of the time you should not use endOf but beginOfNext in Cervval applications
    // ! you can use the standalone endOf(ts, unit) function if needed

    /**
     * Get the TsDate TimeZone Name
     */
    get timeZone(): TimeZoneString;

    /**
     * Get the UnixTimestamp (seconds from the Unix epoch)
     */
    get uts(): UnixTimestamp;

    /**
     * Get the UnixTimestamp value (seconds from the Unix epoch)
     */
    get epochSeconds(): number;

    /**
     * Get the Timestamp value (milliseconds from the Unix epoch)
     */
    get epochMillis(): number;

    /**
     * year*100 + month
     * 
     * (2017-05-25).yearAndMonth //=> 201705
     */
    get yearAndMonth(): number;

    /**
     * weekYear*100 + iso week number
     *
     * (2017-05-25).weekYearAndWeekNumber //=> 201721
     */
    get weekYearAndWeekNumber(): number;

    /**
     * Get the year*1000 + ordinal (meaning the day of the year)
     *
     * (2017-05-25).yearAndOrdinal //=> 2017145
     */
    get yearAndOrdinal(): number;

    /**
     * @returns false if date is invalid
     */
    get isValid(): boolean;

    /** @returns date offset in minutes from UTC */
    get offset(): number;

}

/** Most TS functions accept either a UnixTimestamp or a TsDate */
export type TsParam = TsDate | UnixTimestamp;

/**
 * This property is used to recognize Wrapped TsDate instances
 */
export const IS_TS_DATE_PROPERTY = "__isTsDate";

//#region Casting Methods

/**
 * Get number value from UnixTimestamp
 * @returns {number}
 */
export function valueOf(ts: UnixTimestamp): number {
    return ts as unknown as number;
}

/**
 * Get number value from UnixTimestamp or TsDate
 * @returns {number}
 */
export function tsValueOf(ts: TsParam): number {
    if (typeof ts === "number") { return valueOf(ts); }
    return valueOf((ts as TsDate).uts);
}

/**
 * Get UnixTimestamp value from tsParam
 * @returns {UnixTimestamp}
 */
export function tsParamUts(ts: TsParam): UnixTimestamp {
    if (typeof ts === "number") { return ts; }
    return ts.uts;
}

/**
 * Get number values from UnixTimestamp array
 * @returns {number[]}
 */
export function valuesOf(tsArray: UnixTimestamp[]): number[] {
    return tsArray as unknown as number[];
}

/**
 * Get number value from UnixTimestamp or null
 * @returns {number | null}
 */
export function valueOfOrNull(ts: UnixTimestamp | null | undefined): number | null {
    return ts == null ? null : valueOf(ts);
}

/**
 * Get number value from UnixTimestamp or max
 * @returns {number}
 */
export function valueOfOrMax(ts: UnixTimestamp | null | undefined): number {
    return ts == null ? Number.MAX_SAFE_INTEGER : valueOf(ts);
}

/**
 * Cast number to UnixTimestamp 
 * @returns {UnixTimestamp}
 */
export function toTimestamp(utcUnixSeconds: number): UnixTimestamp {
    return utcUnixSeconds as unknown as UnixTimestamp;
}

/**
 * Cast number to UnixTimestamp if not null
 * @returns {UnixTimestamp | null}
 */
export function toTimestampOrNull(utcUnixSeconds: number | null | undefined): UnixTimestamp | null {
    if (utcUnixSeconds == null) {
        return null;
    }
    return utcUnixSeconds as unknown as UnixTimestamp;
}

/** Check if TsDateUnit and cast if true  */
export function isAgendaMode(unit: TsDateUnit): unit is AgendaMode {
    for (const mode of AGENDA_MODES) {
        if (unit === mode) { return true; }
    }
    return false;
}

//#endregion


//#region DateTime Formats

// https://devhints.io/wip/intl-datetime
export type TsDateFormat = string | Intl.DateTimeFormatOptions;

// Parsing tokens
// https://moment.github.io/luxon/#/parsing?id=table-of-tokens

/** y-MM-dd HH:mm:ss */
export const MYSQL_DATE_FORMAT = "y-MM-dd HH:mm:ss";// 2019-09-01 14:28:00
/** y-MM-dd HH:mm:ss */
export const TIMESTAMP_FORMAT = MYSQL_DATE_FORMAT;// 2019-09-01 14:28:00
/** y-MM-dd */
export const TIMESTAMP_DATE_ONLY_FORMAT = "y-MM-dd";// 2019-09-01
/** 
 * @deprecated prefer toISO() method (this format will print +00:00 instead of final Z for UTC)
 * "yyyy-MM-dd'T'HH:mm:ss.SSSZZ"
 */
export const ISO_8601_FORMAT = "yyyy-MM-dd'T'HH:mm:ss.SSSZZ"; // 2019-09-01T14:28:00.000+02:00
/** dd/MM/yyyy ! allows year on six-digits (e.g. 09/01/2019, 09/01/201923) */
export const DEFAULT_DATE_FORMAT = "D";// 09/01/2019
/** dd/MM/yy */
export const DEFAULT_SHORT_DATE_FORMAT: TsDateFormat = { year: "2-digit", month: "numeric", day: "numeric" };// US 10/14/83 FR 14/10/83
/** cccc dd MMMM yyyy */
export const DEFAULT_LONG_DATE_FORMAT = "DDDD";// mardi 9 janvier 2019
/** HH:mm */
export const DEFAULT_HOUR_MINUTE_FORMAT = "t";// US 1:30 PM FR 13:30
/** HH:mm:ss */
export const DEFAULT_HOUR_MINUTE_SECONDS_FORMAT = "tt";// 14:28:25
/** dd/MM/yyyy HH:mm */
export const DEFAULT_DATETIME_FORMAT = "D t";// 09/01/2019 14:28
/** dd/MM/yyyy HH:mm:ss */
export const DEFAULT_DATETIME_SECONDS_FORMAT = "D tt";// 09/01/2019 14:28:25
/** HH:mm dd/MM/yyyy */
export const DEFAULT_TIMEDATE_FORMAT = "t D";// 14:28 09/01/2019
/** cccccdd/MM
 * Warning T can be tuesday or thursday/ Attention M peut être mardi ou mercredi */
export const DAY_MONTH_DATE_WITH_FIRST_DAY_LETTER = "cccccdd/MM"; // S01/09
/** ccc dd/MM */
export const DAY_MONTH_DATE_WITH_SHORT_DAY = "ccc dd/MM"; // Sun 01/09
/** ccc dd/MM/yy */
export const DAY_MONTH_YEAR_DATE_WITH_SHORT_DAY = "ccc dd/MM/yy"; // Sun 01/09/19

//#endregion DateTime Formats

//#region Loose Parse Formats
export const DEFAULT_FR_LOOSE_PARSE_DATE_FORMAT = "d/M/y";// 9/1/19 => allow loose parsing
export const DEFAULT_FR_LOOSE_PARSE_HOUR_MINUTE_FORMAT = "H:m";// 4:8 => 04:08 allow loose parsing
export const DEFAULT_FR_LOOSE_PARSE_DATETIME_FORMAT = DEFAULT_FR_LOOSE_PARSE_DATE_FORMAT + " " + DEFAULT_FR_LOOSE_PARSE_HOUR_MINUTE_FORMAT;// 9/1/19 4:8 => allow loose parsing

export const DEFAULT_US_LOOSE_PARSE_DATE_FORMAT = "M/d/y";// 9/1/19 => allow loose parsing
export const DEFAULT_US_LOOSE_PARSE_HOUR_MINUTE_FORMAT = "h:m a";// 4:8 AM => 04:08 AM allow loose parsing
export const DEFAULT_US_LOOSE_PARSE_DATETIME_FORMAT = DEFAULT_US_LOOSE_PARSE_DATE_FORMAT + " " + DEFAULT_US_LOOSE_PARSE_HOUR_MINUTE_FORMAT;// 9/1/19 4:8 AM => allow loose parsing

export const LOOSE_PARSE_OPTIONS = {
    "fr-FR": {
        [DEFAULT_DATE_FORMAT]: DEFAULT_FR_LOOSE_PARSE_DATE_FORMAT,
        [DEFAULT_HOUR_MINUTE_FORMAT]: DEFAULT_FR_LOOSE_PARSE_HOUR_MINUTE_FORMAT,
        [DEFAULT_DATETIME_FORMAT]: DEFAULT_FR_LOOSE_PARSE_DATETIME_FORMAT
    },
    "en-US": {
        [DEFAULT_DATE_FORMAT]: DEFAULT_US_LOOSE_PARSE_DATE_FORMAT,
        [DEFAULT_HOUR_MINUTE_FORMAT]: DEFAULT_US_LOOSE_PARSE_HOUR_MINUTE_FORMAT,
        [DEFAULT_DATETIME_FORMAT]: DEFAULT_US_LOOSE_PARSE_DATETIME_FORMAT
    }
};

export interface LooseParsingOptions {
    /** 
     * If doNotConvert2DigitsYear is NOT set to true a year of 25 will be transformed to 2025 (00 to 60 => 20xx, 61 to 99 => 19xx) 
     * Note that even if doNotConvert2DigitsYear is false, for loose year parsing to work you need to have a compatible format string (default is ok)
     */
    doNotConvert2DigitsYear?: boolean;

    /**
     * If doNotTryLooseParsing is NOT set to true and you are using default formats, a loose parsing is allowed (1/1/10 => 01/01/2010)
     */
    doNotTryLooseParsing?: boolean;
}

//#endregion Loose Parse Formats

//#region Settings

/**
 * Define settings for timestamps tools
 */
export const TimestampsSettings: {

    /** 
     * Override this date to change the 'today' date returned by Timestamp tools
     */
    overrideTodayDate?: TsDate,


    /**
     * System date locale (saved when calling TimestampsTools.setDefaultDateLocale for the first time)
     * ! do NOT set directly, use TimestampsTools.setDefaultDateLocale
     */
    systemDateLocale?: DateLocale,

    /**
     * Application default date locale
     * ! do NOT set directly, use TimestampsTools.setDefaultDateLocale
     */
    defaultDateLocale?: DateLocale,

    /** [default 1h] After this duration in seconds, cache is cleared */
    cacheExpiresAfterSeconds: number,
    /** true => cache is disabled */
    disableParseCache?: boolean,
    /** true => cache is disabled */
    disableFormatCache?: boolean
} = {
    cacheExpiresAfterSeconds: SECONDS_IN_ONE_DAY // one day
};

//#endregion Settings